import React, { useCallback } from 'react'
import { useAppSelector } from 'reduxTyped'
import { l, Checkbox } from '@manychat/manyui'

import { getBotTimezone } from 'common/core/selectors/appSelectors'
import { FieldSegmentType } from 'common/fields/entities/enums'
import { Field } from 'common/fields/entities/field'
import {
  FilterCondition,
  FilterFieldOperatorData,
  BetweenDatesConditionValue,
} from 'common/filter/interfaces'
import { InputDisplayType } from 'common/filter/models/AudienceFilter/constants'
import { ConditionTagPicker } from 'common/tags/components/ConditionTagPicker/ConditionTagPicker'

import { DateControl } from './components/DateControl'
import { DelayControl } from './components/DelayControl'
import { FieldValuePicker } from './components/FieldValuePicker'
import { OnChangeHandler } from './components/FieldValuePicker/interfaces'
import { NumberInput } from './components/NumberInput'
import { TextInput } from './components/TextInput'
import { TimeBetween } from './components/TimeBetween'
import { useConditionValueHandlers } from './hooks/useConditionValueHandlers'

interface ConditionValueProps {
  condition: FilterCondition
  fieldDesc: Field
  operatorDesc: FilterFieldOperatorData
  isBuilder?: boolean
  allowDateFormula?: boolean
  onChange: (data: Partial<FilterCondition>, closePopover?: boolean) => void
}

export const ConditionValue = ({
  condition,
  fieldDesc,
  operatorDesc,
  isBuilder = false,
  allowDateFormula = false,
  onChange,
}: ConditionValueProps) => {
  const botTimezone = useAppSelector(getBotTimezone)

  const {
    onChange: onChangeGeneral,
    onCurrentTimeFromChange,
    onCurrentTimeToChange,
    onCurrentTimeWeekdaysChange,
    onChangeDatePicker,
    onDelayChange,
    onDelayCheckBoxChange,
  } = useConditionValueHandlers(onChange, condition, botTimezone)

  const handleSensitivityChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ case_sensitive: event.target.checked })
    },
    [onChange],
  )

  if (operatorDesc.display === InputDisplayType.NONE) {
    return null
  }

  if (operatorDesc.display === InputDisplayType.TEXT_INPUT) {
    return (
      <>
        <TextInput
          value={condition.value as string}
          fieldId={fieldDesc.id}
          isBuilder={isBuilder}
          onChange={onChangeGeneral}
        />
        {isBuilder && (
          <Checkbox
            label={l.translate('Case Sensitivity')}
            checked={condition.case_sensitive || false}
            onChange={handleSensitivityChange}
            className="d-flex align-center m-l"
            infoText={l.translate(
              'Leave the box unchecked to interpret upper and lowercase letters as being the same.',
            )}
          />
        )}
      </>
    )
  }

  if (operatorDesc.display === InputDisplayType.NUMBER) {
    return (
      <NumberInput
        value={condition.value as number}
        isBuilder={isBuilder}
        onChange={onChangeGeneral}
      />
    )
  }

  if (operatorDesc.display === InputDisplayType.FIELD_VALUE_PICKER) {
    return condition?.type === FieldSegmentType.TAG ? (
      <ConditionTagPicker
        condition={condition}
        onChange={onChangeGeneral}
        fieldDescription={fieldDesc}
      />
    ) : (
      <FieldValuePicker
        fieldId={fieldDesc.id}
        condition={condition}
        onChange={onChangeGeneral as OnChangeHandler}
        label=""
      />
    )
  }

  if (operatorDesc.display === InputDisplayType.DATE_PICKER) {
    return (
      <DateControl
        condition={condition}
        fieldDesc={fieldDesc}
        allowDateFormula={allowDateFormula}
        onChange={onChange}
        onChangeDatePicker={onChangeDatePicker}
        onChangeGeneral={onChangeGeneral}
        onDelayCheckBoxChange={onDelayCheckBoxChange}
      />
    )
  }

  if (operatorDesc.display === InputDisplayType.DELAY) {
    return <DelayControl value={condition.value as number} onChange={onDelayChange} />
  }

  if (operatorDesc.display === InputDisplayType.SYSTEM_CURRENT_DATE_TIME_BETWEEN) {
    return (
      <TimeBetween
        value={condition.value as BetweenDatesConditionValue}
        onCurrentTimeFromChange={onCurrentTimeFromChange}
        onCurrentTimeToChange={onCurrentTimeToChange}
        onCurrentTimeWeekdaysChange={onCurrentTimeWeekdaysChange}
      />
    )
  }

  return (
    <div>
      <p>
        {l.translate('Field type')}: {fieldDesc.type}
      </p>
      <p>
        {l.translate('Value')}: {String(condition.value)}
      </p>
      <p>
        {l.translate('Operator')}: {condition.operator}
      </p>
    </div>
  )
}
